import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { MomentInput } from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ProjectId, TaskId } from 'api/models';
import { Project, ProjectPhase } from 'api/project';
import {
  StatusGroup,
  Task,
  TaskAPI,
  TaskUpdatePhasePayload,
  TaskCardPosition,
} from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import {
  converToEntityId,
  PhaseEntityType,
} from 'components/organisms/projects/project-plan/phase-entity';
import { Actor, ActorName } from 'data/core';
import { useStatusFieldV2 } from 'data/fields/hooks';
import { saveTaskDetailSaga, updateTaskSagaAction } from 'data/tasks/actions';
import { useTaskPaneUpdateMiniEnabled } from 'hooks/use-task-pane-update-mini';
import { useTranslation } from 'hooks/use-translation';
import { ApiErrorData } from 'queries/error';
import { logger } from 'services';
import { queueMacrotask } from 'utils/macrotask';
import { negativeUid } from 'utils/uid';
import { getHopId, safelyParseJSON } from 'utils/window-utils';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
  ProjectTasksQueryKey,
} from './common';
import { tenMinutes } from './constants';

type Tasks = Task[];

export const useProjectTasksQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<Tasks, unknown, Tasks, ProjectTasksQueryKey> = {},
  config?: AxiosRequestConfig,
): UseQueryResult<Tasks> => {
  return useQuery(
    getProjectTasksQueryKey(projectId),
    () => TaskAPI.getTaskList(projectId[1], config),
    {
      staleTime: tenMinutes,
      enabled: !!projectId[1] && projectId[1] > 0,
      ...options,
    },
  );
};

type TaskDetailQueryKey = [string, number, number];
export const getTaskDetailQueryKey = ([
  ,
  projectId,
  taskId,
]: TaskId): TaskDetailQueryKey => ['task-detail', projectId, taskId];

export const useUpdatePhaseTaskMutation = (
  taskId: number,
): UseMutationResult<Task, unknown, TaskUpdatePhasePayload> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<Task, unknown, TaskUpdatePhasePayload>(
    (payload) => TaskAPI.updatePhase(taskId, payload),
    {
      onSuccess: async (task) => {
        const {
          project: { projectId },
        } = task;

        dispatch(saveTaskDetailSaga(task));
        await queryClient.setQueryData(
          getTaskDetailQueryKey(TaskId(projectId, taskId)),
          task,
        );

        await queryClient.refetchQueries(
          getProjectTasksQueryKey(ProjectId(projectId)),
        );
        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );

        await queryClient.invalidateQueries(
          getProjectDependencyMapKey(projectId),
        );
      },
    },
  );
};

export const useTaskDetailQuery = (
  taskId: TaskId,
  queryOptions: UseQueryOptions<
    Task,
    ApiErrorData,
    Task,
    TaskDetailQueryKey
  > = {},
): UseQueryResult<Task, ApiErrorData> => {
  return useQuery(
    getTaskDetailQueryKey(taskId),
    () => TaskAPI.getTask(taskId[1], taskId[2]),
    {
      retry: 2,
      ...queryOptions,
    },
  );
};

type TaskDetailFieldsQueryKey = [string, number, number];

export const getTaskDetailFieldsQueryKey = ({
  queryString,
  projectId,
  taskId,
}: {
  queryString: string;
  projectId: number;
  taskId: number;
}): TaskDetailFieldsQueryKey => [queryString, projectId, taskId];

export const useTaskDetailsDefaultFieldsQuery = (
  { projectId, taskId },
  queryOptions: UseQueryOptions<
    Task,
    ApiErrorData,
    Task,
    TaskDetailFieldsQueryKey
  > = {},
): UseQueryResult<Task, ApiErrorData> => {
  return useQuery(
    getTaskDetailFieldsQueryKey({
      queryString: 'task-details-default-fields',
      projectId,
      taskId,
    }),
    () => TaskAPI.getTaskDetailsDefaultFields({ projectId, taskId }),
    {
      // staleTime: tenMinutes,
      retry: 2,
      ...queryOptions,
    },
  );
};

export const useTaskDetailsAdditionalFieldsQuery = (
  { projectId, taskId },
  queryOptions: UseQueryOptions<
    Task,
    ApiErrorData,
    Task,
    TaskDetailFieldsQueryKey
  > = {},
): UseQueryResult<Task, ApiErrorData> => {
  return useQuery(
    getTaskDetailFieldsQueryKey({
      queryString: 'task-details-additional-fields',
      projectId,
      taskId,
    }),
    () => TaskAPI.getTaskDetailsAdditionalFields({ projectId, taskId }),
    {
      // staleTime: tenMinutes,
      retry: 2,
      ...queryOptions,
    },
  );
};

export const useCreateTaskOptimisticMutation = (
  projectId: ProjectId,
  phaseId?: number,
) => {
  const queryClient = useQueryClient();
  const taskStatusField = useStatusFieldV2();

  const dispatch = useDispatch();

  const [tasksInCreation, setTasksInCreation] = useState<Tasks>([]);

  const createTask = (payload) => {
    const tempTaskId = negativeUid();
    const taskCardPosition =
      payload.taskCardPosition === TaskCardPosition.Top ? 0 : null;

    setTasksInCreation((prev) => {
      const newTask = {
        ...payload,
        taskId: tempTaskId,
        fields: [
          {
            ...taskStatusField,
            fieldValue: StatusGroup.Todo,
          },
        ],
        project: {
          projectId: projectId[1],
        },
      } as Task;

      if (taskCardPosition === 0) {
        return [newTask, ...prev];
      }

      return [...prev, newTask];
    });

    queueMacrotask(() => {
      payload = {
        ...payload,
        position: taskCardPosition,
      };
      TaskAPI.createTask(projectId[1], payload)
        .then((newTask) => {
          const previousProjectData = queryClient.getQueryData<Project>(
            getProjectDetailQueryKey(projectId),
          );
          const previousProjectTasksData = queryClient.getQueryData<Tasks>(
            getProjectTasksQueryKey(projectId),
          );

          // optimistic update for tasks
          const optimisticTasks = previousProjectTasksData
            ? taskCardPosition === 0
              ? [newTask].concat(previousProjectTasksData)
              : previousProjectTasksData.concat(newTask)
            : [newTask];

          // optimistic update task order for project phase

          let projectPhase: ProjectPhase;
          const optimisticProjectData = {
            ...previousProjectData!,
            projectPhases: previousProjectData!.projectPhases.map((phase) => {
              if (phase.projectPhaseId === phaseId) {
                projectPhase = {
                  ...phase,
                  aggregateProgressValues: {
                    ...phase.aggregateProgressValues,
                    taskCount:
                      (phase?.aggregateProgressValues?.taskCount ?? 0) + 1,
                  },
                  totalTaskCount: (phase.totalTaskCount ?? 0) + 1,
                  entityOrder: phase.entityOrder
                    ? taskCardPosition === 0
                      ? [
                          converToEntityId(
                            newTask.taskId,
                            PhaseEntityType.Task,
                          ),
                          ...phase.entityOrder,
                        ]
                      : [
                          ...phase.entityOrder,
                          converToEntityId(
                            newTask.taskId,
                            PhaseEntityType.Task,
                          ),
                        ]
                    : [converToEntityId(newTask.taskId, PhaseEntityType.Task)],
                };

                return projectPhase;
              }

              return phase;
            }),
          };

          const startDate: MomentInput =
            newTask.startDate || newTask.dueDate || null;
          const dueDate: MomentInput =
            newTask.dueDate || newTask.startDate || null;

          queryClient.setQueryData<Project>(
            getProjectDetailQueryKey(projectId),
            optimisticProjectData,
          );

          queryClient.setQueryData<Tasks>(
            getProjectTasksQueryKey(projectId),
            optimisticTasks as Tasks,
          );

          if (startDate != null && dueDate != null) {
            dispatch(
              updateTaskSagaAction({
                projectId: projectId[1],
                taskId: newTask.taskId,
                data: {
                  startDate,
                  dueDate,
                },
                options: {
                  skipStoreUpdate: true,
                  skipRefetch: true,
                  newTaskCreation: true,
                  skipApiCall: true,
                },
              }),
            );
          }
        })
        .catch((e) => {
          logger.error('Error creating task', { error: e });
          toastService({
            title: 'Failed to create task',
            message: '',
          }).error();
        })
        .finally(() => {
          setTasksInCreation((prev) =>
            prev.filter((t) => t.taskId !== tempTaskId),
          );
        });
    });
  };

  return { tasksInCreation, createTask };
};

type TaskUpdate = [number];
export const useTaskUpdate = ([projectId]: TaskUpdate) => {
  const queryClient = useQueryClient();

  const onFetchTaskSuccess = useCallback(
    (newTask: Task | undefined) => {
      queryClient.setQueryData(
        getProjectTasksQueryKey(ProjectId(projectId)),
        (prevTasks: Task[] | undefined) =>
          newTask
            ? prevTasks?.map((prevTask: Task) => {
                return prevTask.taskId === newTask.taskId ? newTask : prevTask;
              }) ?? []
            : prevTasks ?? [],
      );
    },
    [projectId, queryClient],
  );

  return { onFetchTaskSuccess };
};

export const useTaskPaneUpdate = (taskId: TaskId) => {
  const [, projectId, taskIdFromParam] = taskId;
  const queryClient = useQueryClient();
  const { onFetchTaskSuccess } = useTaskUpdate([projectId]);

  const skipRefetch =
    useTaskPaneUpdateMiniEnabled() ||
    window.location.pathname.includes(`/projects/${projectId}/plan`);

  const taskDetailQueryKey = useMemo(() => {
    return getTaskDetailQueryKey(['task_id', projectId, taskIdFromParam]);
  }, [projectId, taskIdFromParam]);

  const taskDetailFieldsQueryKey = useMemo(() => {
    return getTaskDetailFieldsQueryKey({
      queryString: 'task-details-default-fields',
      projectId,
      taskId: taskIdFromParam,
    });
  }, [projectId, taskIdFromParam]);

  const taskDetailAdditionalFieldsQueryKey = useMemo(() => {
    return getTaskDetailFieldsQueryKey({
      queryString: 'task-details-additional-fields',
      projectId,
      taskId: taskIdFromParam,
    });
  }, [projectId, taskIdFromParam]);

  const onTaskUpdateSuccess = useCallback(
    async (newTask: Task) => {
      queryClient.setQueryData(taskDetailQueryKey, newTask);
      queryClient.setQueryData(taskDetailFieldsQueryKey, newTask);
      queryClient.setQueryData(taskDetailAdditionalFieldsQueryKey, newTask);
    },
    [
      queryClient,
      taskDetailQueryKey,
      taskDetailFieldsQueryKey,
      taskDetailAdditionalFieldsQueryKey,
    ],
  );

  const handleTaskSocketUpdate = useCallback(
    async (socketData) => {
      const {
        data: { taskId: taskIdReceivedFromSocket },
        actor,
        hopId: socketHopId,
      } = socketData;
      const { actorName, actorId } = safelyParseJSON(actor) ?? ({} as Actor);
      const hopId = getHopId();

      if (
        (actorName !== ActorName.TaskDrawer ||
          actorId !== taskIdReceivedFromSocket ||
          hopId !== socketHopId) &&
        taskIdReceivedFromSocket === taskIdFromParam
      ) {
        queryClient.refetchQueries(taskDetailQueryKey);
      }
    },
    [taskDetailQueryKey, taskIdFromParam, queryClient],
  );

  return {
    onFetchTaskSuccess,
    handleTaskSocketUpdate,
    onTaskUpdateSuccess,
    skipRefetch,
  };
};

export const useDeleteTaskAttachmentMutation = () => {
  const attachmentDeletedSuccessTitle = useTranslation(
    'project.files.deleted.success.title',
  );
  const attachmentDeletedSuccessMessage = useTranslation(
    'project.files.deleted.success.message',
  );
  const attachmentDeletedErrorMessage = useTranslation(
    'project.files.deleted.error.message',
  );

  return useMutation(
    (attachmentId: number) => TaskAPI.deleteTaskAttachment(attachmentId),
    {
      onSuccess: () => {
        toastService({
          title: attachmentDeletedSuccessTitle,
          message: attachmentDeletedSuccessMessage,
        }).success();
      },
      onError: () => {
        toastService({
          title: 'Error',
          message: attachmentDeletedErrorMessage,
        }).error();
      },
    },
  );
};
